import type { Row, Worksheet } from 'exceljs';
import type { StatisticCell } from '../statisticCells';
import type {
  StatisticsExportMode,
  StatisticsExportRow,
  StatisticsExportSection,
} from './statisticsExportModel';

type SectionModeRowWriter = (
  row: Row,
  initial: StatisticsExportRow,
  rowIndex: number,
) => readonly StatisticsExportRow[];

export const getStatisticsCellFormat = (metric: StatisticCell): string =>
  typeof metric.value === 'string'
    ? '@'
    : metric.value instanceof Date
      ? 'dd.mm.yyyy'
      : (metric.numFmt ?? 'General');

export const addStatisticsModeSelector = (
  sheet: Worksheet,
  section: StatisticsExportSection,
  titleRow: number,
): SectionModeRowWriter | undefined => {
  if (!section.modes) return undefined;
  const modes = section.modes;
  modes.forEach((mode, index) => {
    const label = sheet.getCell(titleRow, 5 + index);
    label.value = mode.label;
    label.numFmt = '@';
  });
  for (let column = 5; column <= 4 + modes.length * 3; column += 1) {
    sheet.getColumn(column).hidden = true;
  }
  const lastLabelColumn = sheet.getColumn(4 + modes.length).letter;
  const labels = `$E$${titleRow}:$${lastLabelColumn}$${titleRow}`;
  sheet.getCell(titleRow, 4).dataValidation = {
    type: 'list',
    allowBlank: false,
    formulae: [labels],
    showErrorMessage: true,
    errorStyle: 'stop',
    errorTitle: 'Выберите режим',
    error: 'Выберите один из режимов в списке.',
    showInputMessage: true,
    promptTitle: 'Режим статистики',
    prompt: 'Выберите режим «Задания» или «Звонки».',
  };
  const selectionIndex = modes.reduceRight((fallback, mode, index) => {
    const column = sheet.getColumn(5 + index).letter;
    return `IF($D$${titleRow}=$${column}$${titleRow},${index + 1},${fallback})`;
  }, 'NA()');
  return createModeRowWriter(sheet, modes, selectionIndex);
};

const createModeRowWriter = (
  sheet: Worksheet,
  modes: readonly StatisticsExportMode[],
  selectionIndex: string,
): SectionModeRowWriter => {
  const setModeFormats = (
    address: string,
    metrics: readonly StatisticCell[],
  ) => {
    const formats = metrics.map(getStatisticsCellFormat);
    if (new Set(formats).size < 2) return;
    sheet.addConditionalFormatting({
      ref: address,
      rules: formats.map((numFmt, index) => ({
        type: 'expression',
        priority: 0,
        formulae: [`${selectionIndex}=${index + 1}`],
        style: { numFmt: numFmt === 'General' ? 'general' : numFmt },
      })),
    });
  };
  return (row, initial, rowIndex) => {
    const variants = modes.map((mode) => mode.rows[rowIndex]);
    variants.forEach((variant, index) => {
      const firstColumn = 5 + index * 3;
      const cells = [
        { value: variant.label },
        variant.cells.value,
        variant.cells.percent,
      ];
      cells.forEach((metric, offset) => {
        const source = row.getCell(firstColumn + offset);
        source.value = metric.value;
        source.numFmt = getStatisticsCellFormat(metric);
      });
    });
    const lastColumn = sheet.getColumn(4 + variants.length * 3).letter;
    const source = `$E${row.number}:$${lastColumn}${row.number}`;
    const cache = [
      initial.label,
      initial.cells.value.value,
      initial.cells.percent.value,
    ];
    cache.forEach((result, offset) => {
      const index = `INDEX(${source},1,(${selectionIndex}-1)*3+${offset + 1})`;
      const cell = row.getCell(offset + 2);
      cell.value = {
        formula: `IF(ISBLANK(${index}),"",${index})`,
        result: result ?? '',
      };
      if (offset > 0) {
        setModeFormats(
          cell.address,
          variants.map((variant) =>
            offset === 1 ? variant.cells.value : variant.cells.percent,
          ),
        );
      }
    });
    return variants;
  };
};
